import { Checkbox, Loader, Text, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react';
import { RuleEditCard } from './Design';
import { RuleEditor } from './Model';
import { MouseEventHandler, useCallback, useEffect, useMemo } from 'react';
import { AnchorButton } from '@root/Design/Primitives';
import { DataGrid } from '@root/Components/DataGrid';
import { ColumnConfig, DataGridProps, IDataSource, ISelectionStrategy } from '@root/Components/DataGrid/Models';
import { EventEmitter, useEventValue } from '@root/Services/EventEmitter';
import { injectable } from 'tsyringe';
import { TagAutomationRule, ResourceGroupRuleOptions } from '@apis/TagManager/model';
import { useFilterValidation } from './RuleDescriber';
import { makeAutoObservable } from 'mobx';
import { BaseAzureResource, IQueryExpr, SelectedResourceTags } from '@apis/Resources/model';
import { postResourcesQuery, QueryField } from '@apis/Resources';
import { queryBuilder, ValuesGroupOtherText } from '@root/Services/QueryExpr';
import { useAsync, useAsyncAbortable } from '@react-hookz/web';
import { FillerSwitch } from '@root/Design/Filler';
import { ArrayDataSource } from '@root/Services/Query/ArrayDataSource';
import { GroupRow, QueryExprGroupHelper } from '@root/Components/DataGrid/QueryExprGroupHelper';
import { EmptyDataText } from '@root/Components/Text/VisibleSpaces';
import { useFmtSvc } from '@root/Services/FormatService';
import { ResourceDetailsOpener } from '@root/Components/Resources/ResourceDetails';
import { useDiMemo } from '@root/Services/DI';

@injectable()
export class ResourceGroupEditor {
    public options: ResourceGroupRuleOptions = {};
    public ruleEditor: RuleEditor = {} as RuleEditor;
    public filter: IQueryExpr = {};
    public rule?: TagAutomationRule = undefined;

    public constructor() {
        makeAutoObservable(this);
    }
    public init(ruleEditor: RuleEditor) {
        const rule = ruleEditor.rule!;
        rule.Parameters ??= {};
        rule.Parameters.Syntax ??= {};
        rule.Parameters.Syntax.AwsOUAccountGroupRuleOptions = {};
        rule.Parameters.Syntax.ResourceGroupRuleOptions ??= {};
        rule.Parameters.Syntax.ResourceGroupRuleOptions.SelectedTags ??= [];
        this.options = rule.Parameters.Syntax.ResourceGroupRuleOptions;
        this.ruleEditor = ruleEditor;
        this.rule = rule;
        return this;
    }

    public setSelectedTags(selectedTags: Iterable<string>) {
        const updates: SelectedResourceTags[] = [];
        const lookup = new Map<string, SelectedResourceTags>();
        for (const item of selectedTags) {
            const { azId: rg, tagKey } = this.parseId(item);
            if (tagKey) {
                let selection = lookup.get(rg);
                if (!selection) {
                    lookup.set(rg, (selection = { ResourceGroup: rg, SelectedTags: [] }));
                    updates.push(selection);
                }
                selection.SelectedTags!.push(tagKey);
            }
        }
        this.options.SelectedTags!.splice(0, Infinity, ...updates);
    }

    public setApplyAllTags(applyAllTags: boolean) {
        this.options.ApplyAllTags = applyAllTags;
    }

    public getErrors() {
        const result: string[] = [];
        return result;
    }

    public createId(azId: string, tagKey: string) {
        return `${azId}\0${tagKey}`;
    }
    public parseId(id: string) {
        const [azId, tagKey] = id.split('\0');
        return { azId, tagKey };
    }
}

export const ResourceGroupActionCard = observer(function ResourceGroupActionCard({ ruleEditor }: { ruleEditor: RuleEditor }) {
    const resourceGroupEditor = useDiMemo(ResourceGroupEditor, [ruleEditor], (r) => r.init(ruleEditor));
    const filterValidityChanged = useFilterValidation(resourceGroupEditor.ruleEditor);

    return (
        <>
            <RuleEditCard title="Select Resource Groups" description="Select Resource Groups tags which should be copied to the groups' resources">
                <div style={{ height: '600px' }}>
                    <ResourceGroupsSelector editor={resourceGroupEditor} />
                </div>
            </RuleEditCard>
        </>
    );
});

type ResourceGroup = {
    subFullname: string;
    subName: string;
    subId: string;
    azId: string;
    groupName: string;
    tagCt: number;
    tagKey: string | null;
    tagValue: string | null;
    id: string;
};
const ResourceGroupsSelector = observer(function ResourceGroupsSelector({ editor }: { editor: ResourceGroupEditor }) {
    const theme = useMantineTheme();

    const fmtSvc = useFmtSvc();

    const selectedResource = useMemo(() => new EventEmitter<BaseAzureResource | undefined>(undefined), []);

    const getGroupData = useCallback(async () => {
        const rawGroups = await queryBuilder<{ Subscription: string; AzureId: string; 'Tags.Key': string; ResourceType: string }>()
            .where((b) => b.model.ResourceType!.eq('Microsoft.Resources/resourceGroups'))
            .select((b) => ({
                subName: b.model.Subscription,
                azId: b.model.AzureId,
                tagKvp: b.values(b.model['Tags.Key'], undefined, ValuesGroupOtherText),
                tagCt: b.count(),
            }))
            .execute(postResourcesQuery);

        const items =
            rawGroups.Results?.map((rg) => {
                const [, , subId, , groupName] = rg.azId!.split('/');
                const [tagKey, tagValue] = rg.tagKvp && rg.tagKvp !== ValuesGroupOtherText ? rg.tagKvp.split('\\\\') : [null, null];
                return {
                    ...rg,
                    id: editor.createId(rg.azId!, tagKey ?? ''),
                    subId,
                    groupName,
                    subFullname: `${rg.subName} (${subId})`,
                    tagKey,
                    tagValue,
                } as ResourceGroup;
            }) ?? [];

        return new ArrayDataSource(items);
    }, []);

    const [{ status, result: resourceGroups }, { execute, abort }] = useAsyncAbortable(getGroupData, null);

    useEffect(() => {
        execute();
        return abort;
    }, []);

    const columns = useMemo<ColumnConfig<ResourceGroup>[]>(
        () => [
            {
                accessor: 'subFullname',
                header: 'Subscription',
                id: 'subscription',
                defaultWidth: 350,
                type: 'string',
                allowGrouping: true,
                sortField: 'subFullname',
                noRemove: true,
                filter: {
                    filterType: 'string',
                    name: 'Subscription',
                    filterField: 'subFullname',
                },
            },
            {
                accessor: 'groupName',
                header: 'Resource Group',
                id: 'resourceGroup',
                defaultWidth: 250,
                cellRenderer: (item) => <ResourceGroupName azureId={item.azId} onSelect={selectedResource.emit} />,
                type: 'string',
                allowGrouping: true,
                noRemove: true,
                filter: {
                    filterType: 'string',
                    name: 'Resource Group',
                    filterField: 'groupName',
                },
            },
            {
                accessor: 'tagKey',
                header: 'Tag Key',
                id: 'tagKey',
                cellRenderer: (item) => (!item.tagKey ? <EmptyDataText text="None" /> : item.tagKey),
                defaultWidth: 150,
                type: 'string',
                allowGrouping: true,
                noRemove: true,
                filter: {
                    filterType: 'string',
                    name: 'Tag Key',
                    filterField: 'tagKey',
                },
            },
        ],
        [resourceGroups]
    );

    const selectionChanged = useMemo(() => EventEmitter.empty(), []);

    const selectionState = useMemo(() => {
        let selectionLookup = new Set(
            editor.options.SelectedTags?.flatMap((t) => t.SelectedTags?.map((v) => `${t.ResourceGroup}\0${v}`) ?? []) ?? []
        );
        const updateSelected = () => {
            editor.setSelectedTags(selectionLookup);
            selectionChanged.emit();
        };
        return {
            isSelected: (item: ResourceGroup) => selectionLookup.has(item.id),
            count: () => selectionLookup.size,
            setSelectAll: async (selected: boolean) => {
                selectionLookup = new Set(
                    selected ? resourceGroups?.items.filter((r) => r.tagKey).map((r) => editor.createId(r.azId!, r.tagKey)) ?? [] : []
                );
                updateSelected();
            },
            getSelectAllValidity: () => undefined,
            getSelected: async () => resourceGroups?.items.filter((r) => selectionLookup.has(r.id)) ?? [],
            setSelected: (item: ResourceGroup, selected: boolean) => {
                if (item.tagKey) {
                    if (selected) {
                        selectionLookup.add(item.id);
                    } else {
                        selectionLookup.delete(item.id);
                    }
                    updateSelected();
                }
            },
        } as ISelectionStrategy<ResourceGroup>;
    }, [editor, resourceGroups]);

    const gridProps = useMemo(() => {
        const queryApi = resourceGroups?.getQueryApi<ResourceGroup>() ?? (async () => ({ Results: [], Count: 0 }));
        const groupDs = new QueryExprGroupHelper(queryApi, queryApi, 50000);
        const getPage: IDataSource<ResourceGroup>['getPage'] = async (start, end, state, parent) => {
            return (await groupDs.getPage(start, end, state, parent)) as { items: ResourceGroup[]; total: number };
        };
        return {
            statePersistence: { key: 'TagAutomationResourceGroupSelector' },
            dataSource: { getPage },
            childAccessor: {
                hasChildren: (item: ResourceGroup) => 'value' in item,
            },
            onModelLoaded: (m) => groupDs.attach(m),
            minimumLoadingMs: 0,
            onRefreshing: async () => {
                await execute();
            },
            rowStyle: (item, idx, hovered) => ({
                backgroundColor: !item.tagKey && !('value' in item) ? theme.colors.gray[1] : hovered ? theme.colors.primary[1] : undefined,
                color: !item.tagKey && !('value' in item) ? theme.colors.gray[5] : undefined,
            }),
            showRefresh: true,
            renderRowSelector: (...params) => <ResourceGroupItemCheckbox params={params} />,
            columns,
            state: {
                groupBy: [{ id: 'subscription', sortDir: 'Asc', sortMode: 'value' }],
                filters: [],
                columns: columns.map((c) => ({ id: c.id, width: c.defaultWidth ?? 200 })),
                sort: [{ id: 'groupName', dir: 'Asc' }],
            },
            filterValueProvider: {
                getValueProvider: (field: QueryField) => {
                    return async (filter) => {
                        const response = await queryApi({
                            Select: [
                                { Alias: 'value', Expr: field },
                                { Alias: 'ct', Expr: { Operation: 'count' } },
                            ],
                            Where: !filter ? undefined : { Operation: 'contains', Operands: [{ Field: field }, { Value: filter }] },
                            Sort: [{ Expr: { field: 'value' }, Direction: 'Asc' }],
                        });
                        return response.Results?.map((r) => (r as unknown as { value: string }).value ?? '') ?? [];
                    };
                },
            },
        } as DataGridProps<ResourceGroup>;
    }, [resourceGroups, selectionState, columns]);

    return (
        <>
            <FillerSwitch loading={status === 'loading'} noData={!resourceGroups?.items.length}>
                {() => (
                    <DataGrid
                        {...gridProps}
                        columns={columns}
                        selectionMode="multiple"
                        onRowClick="select"
                        allowGroupBy
                        allowLoadAllPages
                        hideColumnSelector
                        showCount={false}
                        renderStats={() => (
                            <Text size="sm" color="dimmed">
                                {fmtSvc.formatInt(resourceGroups?.items.length ?? 0)} resource group tags, {selectionState.count()} selected
                            </Text>
                        )}
                        selectionStrategy={selectionState}
                        renderGroupBy={({ item }) => <GroupByRenderer item={item as unknown as GroupRow<ResourceGroup>} />}
                    />
                )}
            </FillerSwitch>
            <ResourceDetails selectedResource={selectedResource} />
        </>
    );
});

function ResourceDetails({ selectedResource }: { selectedResource: EventEmitter<BaseAzureResource | undefined> }) {
    const resource = useEventValue(selectedResource);
    const clear = useCallback(() => selectedResource.emit(undefined), [selectedResource]);
    return (
        <ResourceDetailsOpener
            onClose={clear}
            platform={resource?.CloudPlatform ?? 'Azure'}
            resourceId={resource?.Id}
            resourceType={resource?.ResourceType ?? ''}
            resource={resource}
        />
    );
}

function ResourceGroupName({ azureId, onSelect }: { azureId: string; onSelect: (resource: BaseAzureResource) => void }) {
    const name = azureId.split('/').pop() ?? azureId;
    const [{ status }, { execute }] = useAsync(async (azId: string) =>
        queryBuilder<BaseAzureResource>()
            .where((b) => b.model.AzureId!.eq(azId))
            .execute(postResourcesQuery)
            .then((r) => r.Results?.[0] as BaseAzureResource | undefined)
    );
    const onClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
        (evt) => {
            execute(azureId).then((res) => {
                if (res) {
                    onSelect(res);
                }
            });
            evt.preventDefault();
        },
        [azureId, execute, onSelect]
    );
    return <AnchorButton onClick={onClick} text={name} iconPosition="right" icon={status !== 'loading' ? null : <Loader size="xs" />} size="sm" />;
}

function ResourceGroupItemCheckbox({
    params: [item, state],
}: {
    params: Parameters<NonNullable<DataGridProps<ResourceGroup>['renderRowSelector']>>;
}) {
    return item !== null && !item?.tagKey ? (
        <></>
    ) : item === null ? (
        <Checkbox
            className="no-animation"
            size="xs"
            onChange={state.some ? () => state.toggle(false) : () => state.toggle(true)}
            checked={state.all}
            indeterminate={state.some && !state.all}
        />
    ) : (
        <Checkbox className="no-animation" size="xs" onChange={(e) => state.toggle(e.currentTarget.checked)} checked={state.selected} />
    );
}

function GroupByRenderer({ item }: { item: { value: string; count: number } }) {
    return item.value === ValuesGroupOtherText ? (
        <>
            <EmptyDataText text="None" />
            &nbsp; ({item.count})
        </>
    ) : (
        <Text size="sm" sx={{ display: 'flex', alignItems: 'center' }}>
            {item.value} ({item.count})
        </Text>
    );
}
